import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Heart, Plus, Tag } from "lucide-react";
import { motion } from "framer-motion";

const OCCASIONS = [
  { key: "", label: "All" },
  { key: "birthday", label: "Birthday" },
  { key: "anniversary", label: "Anniversary" },
  { key: "proposal", label: "Proposal" },
  { key: "surprise", label: "Surprise" },
  { key: "romantic", label: "Romantic" },
];
const PRICES = [
  { label: "Any price", v: 0 },
  { label: "≤ ₹3,000", v: 3000 },
  { label: "≤ ₹5,000", v: 5000 },
  { label: "≤ ₹8,000", v: 8000 },
  { label: "≤ ₹10,000", v: 10000 },
];

export default function Decorations() {
  const { addItem } = useCart();
  const [occasion, setOccasion] = useState("");
  const [maxPrice, setMaxPrice] = useState(0);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["decorations", occasion, maxPrice],
    queryFn: async () =>
      (await api.get("/decorations", {
        params: { ...(occasion ? { occasion } : {}), ...(maxPrice ? { max_price: maxPrice } : {}) },
      })).data,
  });

  const add = (d) =>
    addItem(
      { id: d.id, name: d.name, price: d.price, unit: "decoration", image: d.image },
      { id: "bookmyevent-decor", name: "Book My Event Decor" }
    );

  return (
    <div className="px-4 md:px-8 py-6 pb-24">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center">
          <Heart className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="overline text-primary">Budget-friendly · ₹2,000 – ₹10,000</p>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Surprise Decorations</h1>
        </div>
      </div>
      <p className="text-muted-foreground text-sm mb-5">
        Ready-made decorations for birthdays, anniversaries, proposals & surprises — pick, add & book in minutes.
      </p>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
        {OCCASIONS.map((o) => (
          <button
            key={o.key}
            data-testid={`decor-occasion-${o.key || "all"}`}
            onClick={() => setOccasion(o.key)}
            className={`shrink-0 px-4 h-9 rounded-full text-sm font-bold border transition-colors ${
              occasion === o.key ? "bg-primary text-white border-primary" : "border-stone-300 text-muted-foreground hover:bg-muted"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
        {PRICES.map((p) => (
          <button
            key={p.v}
            data-testid={`decor-price-${p.v}`}
            onClick={() => setMaxPrice(p.v)}
            className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1 ${
              maxPrice === p.v ? "bg-secondary/20 border-secondary text-secondary-foreground" : "border-stone-200 text-muted-foreground hover:bg-muted"
            }`}
          >
            <Tag className="h-3 w-3" /> {p.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading decorations...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="no-decor">No decorations match your filters.</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((d, i) => (
            <motion.div
              key={d.id}
              data-testid={`decor-card-${d.id}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.04, 0.3) }}
              className="bg-card border border-stone-200 rounded-2xl overflow-hidden flex flex-col"
            >
              <div className="relative h-36">
                <img src={imgUrl(d.image)} alt={d.name} className="h-full w-full object-cover" />
                <span className="absolute top-2 left-2 bg-white/95 text-xs font-extrabold px-2 py-1 rounded-lg">{inr(d.price)}</span>
              </div>
              <div className="p-3 flex flex-col flex-1">
                <h3 className="font-display font-bold leading-tight">{d.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 flex-1">{d.desc}</p>
                <div className="flex flex-wrap gap-1 mt-2 mb-3">
                  {(d.occasions || []).slice(0, 3).map((o) => (
                    <span key={o} className="text-[10px] font-semibold bg-muted text-muted-foreground px-2 py-0.5 rounded-full capitalize">{o}</span>
                  ))}
                </div>
                <button
                  data-testid={`decor-add-${d.id}`}
                  onClick={() => add(d)}
                  className="w-full h-10 bg-primary text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add to cart
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
